import { useState } from 'react'
import { Plus, X, Bot } from 'lucide-react'
import type { Competitor } from '@ispot/shared'

interface SuggestionsInboxProps {
  suggestions: Competitor[]
  onConfirm: (id: string) => Promise<void>
  onDismiss: (id: string) => Promise<void>
}

export function SuggestionsInbox({ suggestions, onConfirm, onDismiss }: SuggestionsInboxProps) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())
  const [confirmed, setConfirmed] = useState<Set<string>>(new Set())

  const visible = suggestions.filter(s => !dismissed.has(s.id) && !confirmed.has(s.id))

  if (visible.length === 0) return null

  async function handleConfirm(id: string) {
    setConfirmed(prev => new Set(prev).add(id))
    try {
      await onConfirm(id)
    } catch {
      setConfirmed(prev => { const next = new Set(prev); next.delete(id); return next })
    }
  }

  async function handleDismiss(id: string) {
    setDismissed(prev => new Set(prev).add(id))
    try {
      await onDismiss(id)
    } catch {
      setDismissed(prev => { const next = new Set(prev); next.delete(id); return next })
    }
  }

  return (
    <div style={{
      margin: '0 0 16px',
      padding: '12px 16px',
      background: 'rgba(0,180,190,0.06)',
      border: '1px solid rgba(0,180,190,0.2)',
      borderRadius: '10px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
        <Bot size={14} style={{ color: 'var(--color-market)' }} />
        <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--color-market)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          Found in your research ({visible.length})
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {visible.map(s => (
          <SuggestionRow
            key={s.id}
            competitor={s}
            onConfirm={() => handleConfirm(s.id)}
            onDismiss={() => handleDismiss(s.id)}
          />
        ))}
      </div>
    </div>
  )
}

interface SuggestionRowProps {
  competitor: Competitor
  onConfirm: () => void
  onDismiss: () => void
}

function SuggestionRow({ competitor, onConfirm, onDismiss }: SuggestionRowProps) {
  const description = competitor.whatTheyDo
    ? competitor.whatTheyDo.length > 100
      ? competitor.whatTheyDo.slice(0, 100) + '…'
      : competitor.whatTheyDo
    : null

  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      padding: '8px 10px',
      background: 'white',
      borderRadius: '8px',
      border: '1px solid var(--color-border-subtle)',
    }}>
      {/* Add button — primary action, prominent */}
      <button
        onClick={onConfirm}
        title="Add to competitor map"
        style={{
          width: '28px',
          height: '28px',
          flexShrink: 0,
          marginTop: '1px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--color-market)',
          border: 'none',
          borderRadius: '7px',
          color: 'white',
          cursor: 'pointer',
        }}
      >
        <Plus size={14} strokeWidth={2.5} />
      </button>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-text)' }}>
            {competitor.name}
          </span>
          {competitor.category && (
            <span style={{ fontSize: '10px', padding: '1px 6px', background: 'rgba(9,132,227,0.15)', color: 'var(--color-business)', borderRadius: '10px', fontWeight: 600 }}>
              {competitor.category}
            </span>
          )}
        </div>
        {description && (
          <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginTop: '2px' }}>
            {description}
          </div>
        )}
      </div>

      {/* Dismiss — secondary, subtle */}
      <button
        onClick={onDismiss}
        title="Dismiss suggestion"
        style={{
          padding: '4px',
          flexShrink: 0,
          marginTop: '2px',
          background: 'none',
          border: 'none',
          borderRadius: '4px',
          color: 'var(--color-text-muted)',
          cursor: 'pointer',
        }}
      >
        <X size={14} />
      </button>
    </div>
  )
}
